import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../modules/utils/fetchWithHandlingLoginRedirect'

interface ChangedDetail {
  canUndo?: boolean
  canRedo?: boolean
}

interface DraftStatusResponse {
  result: {
    has_draft: boolean
  }
}

export default class DraftControlsController extends Controller<HTMLElement> {
  static targets = ['discard', 'undo', 'redo', 'liveVersion', 'draftVersion']

  static values = {
    draftStatusUrl: String
  }

  declare readonly discardTarget: HTMLButtonElement
  declare readonly hasDiscardTarget: boolean
  declare readonly undoTarget: HTMLButtonElement
  declare readonly hasUndoTarget: boolean
  declare readonly redoTarget: HTMLButtonElement
  declare readonly hasRedoTarget: boolean
  declare readonly liveVersionTarget: HTMLElement
  declare readonly hasLiveVersionTarget: boolean
  declare readonly draftVersionTarget: HTMLElement
  declare readonly hasDraftVersionTarget: boolean
  declare readonly draftStatusUrlValue: string

  private draftStatusRequestId = 0

  connect (): void {
    const mod = /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent) ? 'Cmd' : 'Ctrl'

    if (this.hasUndoTarget) {
      this.appendShortcutHint(this.undoTarget, `${mod}+Z`)
    }

    if (this.hasRedoTarget) {
      this.appendShortcutHint(this.redoTarget, `${mod}+Shift+Z`)
    }
  }

  private appendShortcutHint (button: HTMLButtonElement, hint: string): void {
    button.title = `${button.title} (${hint})`

    const label = button.getAttribute('aria-label')

    if (label) {
      button.setAttribute('aria-label', `${label} (${hint})`)
    }
  }

  async markChanged (event: CustomEvent<ChangedDetail>): Promise<void> {
    if (this.hasUndoTarget) {
      this.undoTarget.disabled = event.detail?.canUndo !== true
    }

    if (this.hasRedoTarget) {
      this.redoTarget.disabled = event.detail?.canRedo !== true
    }

    await this.refreshDraftStatus()
  }

  private async refreshDraftStatus (): Promise<void> {
    if (!this.draftStatusUrlValue) {
      return
    }

    const requestId = ++this.draftStatusRequestId

    try {
      const response = await fetchWithHandlingLoginRedirect(this.draftStatusUrlValue)
      const body = await response.json() as DraftStatusResponse

      if (requestId === this.draftStatusRequestId) {
        this.applyDraftStatus(body.result.has_draft)
      }
    } catch {}
  }

  private applyDraftStatus (isDraft: boolean): void {
    if (this.hasDiscardTarget) {
      this.discardTarget.disabled = !isDraft
    }

    if (this.hasLiveVersionTarget) {
      this.liveVersionTarget.hidden = isDraft
    }

    if (this.hasDraftVersionTarget) {
      this.draftVersionTarget.hidden = !isDraft
    }
  }

  requestUndo (): void {
    this.dispatch('undo', { target: window, prefix: 'draft-controls' })
  }

  requestRedo (): void {
    this.dispatch('redo', { target: window, prefix: 'draft-controls' })
  }

  /**
   * Cmd/Ctrl+Z undoes (Shift+Z redoes), Cmd/Ctrl+Y also redoes — the same shortcuts as the buttons,
   * available regardless of where focus is on the page. Bound at the document level rather than
   * scoped to the buttons themselves, since neither key needs the toolbar to be focused. Skipped
   * while a text field is focused, so it doesn't hijack the browser's native undo of a keystroke.
   */
  handleKeydown (event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || this.isEditingText(event.target)) {
      return
    }

    const key = event.key.toLowerCase()

    if (key === 'z') {
      event.preventDefault()

      if (event.shiftKey) {
        this.triggerRedo()
      } else {
        this.triggerUndo()
      }
    } else if (key === 'y') {
      event.preventDefault()
      this.triggerRedo()
    }
  }

  private isEditingText (target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false
    }

    return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable || target.closest('[contenteditable="true"]') !== null
  }

  private triggerUndo (): void {
    if (this.hasUndoTarget && !this.undoTarget.disabled) {
      this.requestUndo()
    }
  }

  private triggerRedo (): void {
    if (this.hasRedoTarget && !this.redoTarget.disabled) {
      this.requestRedo()
    }
  }
}
